import {
  AlertTriangle,
  Bug,
  Camera,
  Car,
  CheckCircle,
  Clock,
  CreditCard,
  HelpCircle,
  Mail,
  MessageSquare,
  Phone,
  Shield,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";

import type { EmergencyContact } from "@/types/landing";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { submitIssueReport } from "@/services/apiService";
import { EmergencyContactsService } from "@/services/landing/emergencyContactsService";

export default function ReportIssue() {
  const [emergencyContacts, setEmergencyContacts] = useState<
    EmergencyContact[]
  >([]);
  const [contactsLoading, setContactsLoading] = useState(true);

  const [formData, setFormData] = useState({
    attachments: null as FileList | null,
    description: "",
    email: "",
    issueType: "",
    location: "",
    phone: "",
    priority: "",
    subject: "",
    tripId: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [apiError, setApiError] = useState<null | string>(null);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, type, value } = e.target;
    if (type === "file") {
      const fileInput = e.target as HTMLInputElement;
      setFormData((prev) => ({
        ...prev,
        [name]: fileInput.files,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError(null);

    try {
      const result = await submitIssueReport({
        ...formData,
        attachments: formData.attachments
          ? Array.from(formData.attachments)
          : undefined,
      });
      if (result.success) {
        setIsSubmitted(true);
      } else {
        setApiError(result.message || "Failed to submit report.");
      }
    } catch (error) {
      // Type narrowing for error
      let message = "Failed to submit report.";
      if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message?: unknown }).message === "string"
      ) {
        message = (error as { message: string }).message;
      }
      setApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const issueTypes = [
    { icon: <Shield size={20} />, label: "Safety Concern", value: "safety" },
    {
      icon: <CreditCard size={20} />,
      label: "Payment Issue",
      value: "payment",
    },
    { icon: <User size={20} />, label: "Driver Behavior", value: "driver" },
    { icon: <Car size={20} />, label: "Vehicle Problem", value: "vehicle" },
    { icon: <Bug size={20} />, label: "App Technical Issue", value: "app" },
    {
      icon: <HelpCircle size={20} />,
      label: "Account Problem",
      value: "account",
    },
    { icon: <MessageSquare size={20} />, label: "Other", value: "other" },
  ];

  const priorityLevels = [
    { color: "text-success", label: "Low", value: "low" },
    { color: "text-warning", label: "Medium", value: "medium" },
    { color: "text-error", label: "High", value: "high" },
    { color: "text-error font-bold", label: "Urgent", value: "urgent" },
  ];

  const quickActions = [
    {
      action: "Call 121",
      description: "For immediate safety concerns",
      icon: <Phone className="text-error" size={24} />,
      title: "Emergency Assistance",
      urgent: true,
    },
    {
      action: "Start Chat",
      description: "Get instant help from our team",
      icon: <MessageSquare className="text-primary" size={24} />,
      title: "Live Chat Support",
      urgent: false,
    },
    {
      action: "Call Now",
      description: "Speak directly with support",
      icon: <Phone className="text-secondary" size={24} />,
      title: "Call Support",
      urgent: false,
    },
  ];

  if (isSubmitted) {
    return (
      <>
        <AppNavbar />
        <div className="min-h-screen bg-base-200 pt-16">
          <Breadcrumbs />
          <div className="flex items-center justify-center px-4 py-16">
            <div className="max-w-lg text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-success/20">
                <CheckCircle className="text-success" size={40} />
              </div>
              <h1 className="mb-4 text-3xl font-bold text-success">
                Issue Reported Successfully!
              </h1>
              <p className="mb-6 text-base-content/70">
                Thank you for reporting this issue. Weve received your report
                and our team will investigate it promptly.
              </p>
              <div className="space-y-3">
                <div className="alert alert-info">
                  <Mail size={20} />
                  <div>
                    <h4 className="font-semibold">Ticket Created</h4>
                    <p className="text-sm">
                      Reference: #HR{Math.floor(Math.random() * 100000)}
                    </p>
                  </div>
                </div>
                <div className="alert alert-warning">
                  <Clock size={20} />
                  <div>
                    <h4 className="font-semibold">Response Time</h4>
                    <p className="text-sm">Well respond within 24 hours</p>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  Report Another Issue
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AppNavbar />
      <div className="min-h-screen bg-base-200 pt-16">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="bg-base-200 py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-300 sm:mb-6 sm:h-20 sm:w-20 lg:h-24 lg:w-24">
                <AlertTriangle className="h-8 w-8 text-warning sm:h-10 sm:w-10 lg:h-12 lg:w-12" />
              </div>
              <h1 className="mb-3 text-3xl font-bold sm:mb-4 sm:text-4xl md:text-5xl lg:text-6xl">
                Report an Issue
              </h1>
              <p className="mx-auto max-w-2xl text-base opacity-90 sm:text-lg lg:text-xl">
                We are here to help. Report any issues or concerns and our
                support team will assist you promptly.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="bg-base-100 py-8 sm:py-12">
          <div className="mx-auto max-w-7xl px-4">
            <h2 className="mb-6 text-center text-xl font-bold sm:mb-8 sm:text-2xl">
              Need Immediate Help?
            </h2>
            <div className="grid gap-4 sm:gap-6 md:grid-cols-3">
              {quickActions.map((action, index) => (
                <div
                  className={`card ${action.urgent ? "border-2 border-error" : ""} bg-base-100 shadow-lg transition-shadow hover:shadow-xl`}
                  key={index}
                >
                  <div className="card-body p-4 text-center sm:p-6">
                    <div className="mx-auto mb-3 sm:mb-4">{action.icon}</div>
                    <h3 className="card-title justify-center text-base sm:text-lg">
                      {action.title}
                    </h3>
                    <p className="mb-3 text-xs text-base-content/70 sm:mb-4 sm:text-sm">
                      {action.description}
                    </p>
                    {/* <button
                      className={`btn ${action.urgent ? "btn-error" : "btn-primary"} btn-sm`}
                    >
                      {action.action}
                    </button> */}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Report Form */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-4xl px-4">
            <div className="card bg-base-100 shadow-2xl">
              <div className="card-body p-4 sm:p-6 lg:p-8">
                <h2 className="card-title mb-4 text-2xl sm:mb-6 sm:text-3xl">
                  Submit Issue Report
                </h2>

                <form
                  className="space-y-4 sm:space-y-6"
                  onSubmit={handleSubmit}
                >
                  {/* Issue Type */}
                  <div className="form-control">
                    <label className="label pb-1">
                      <span className="label-text text-sm font-medium sm:text-base lg:text-lg">
                        What type of issue are you reporting?
                      </span>
                    </label>
                    <div className="grid gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3">
                      {issueTypes.map((type) => (
                        <label
                          className="label cursor-pointer justify-start gap-2 rounded-lg border p-3 hover:bg-base-200 sm:gap-3 sm:p-4"
                          key={type.value}
                        >
                          <input
                            className="radio-primary radio radio-sm sm:radio-md"
                            name="issueType"
                            onChange={handleInputChange}
                            required
                            type="radio"
                            value={type.value}
                          />
                          <div className="flex items-center gap-2">
                            {type.icon}
                            <span className="label-text text-xs sm:text-sm">
                              {type.label}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Priority Level */}
                  <div className="form-control">
                    <label className="label pb-1">
                      <span className="label-text text-sm font-medium sm:text-base lg:text-lg">
                        Priority Level
                      </span>
                    </label>
                    <select
                      className="select select-bordered select-sm w-full sm:select-md"
                      name="priority"
                      onChange={handleInputChange}
                      required
                      value={formData.priority}
                    >
                      <option value="">Select priority level</option>
                      {priorityLevels.map((priority) => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Contact Information */}
                  <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                    <div className="form-control">
                      <label className="label pb-1">
                        <span className="label-text text-sm font-medium sm:text-base">
                          Email Address
                        </span>
                      </label>
                      <input
                        className="input input-sm input-bordered w-full sm:input-md"
                        name="email"
                        onChange={handleInputChange}
                        placeholder="bokang@hareeng.org"
                        required
                        type="email"
                        value={formData.email}
                      />
                    </div>
                    <div className="form-control">
                      <label className="label pb-1">
                        <span className="label-text text-sm font-medium sm:text-base">
                          Phone Number
                        </span>
                      </label>
                      <input
                        className="input input-sm input-bordered w-full sm:input-md"
                        name="phone"
                        onChange={handleInputChange}
                        placeholder="+266 5048 0814"
                        type="tel"
                        value={formData.phone}
                      />
                    </div>
                  </div>

                  {/* Trip/Reference Information */}
                  <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                    <div className="form-control">
                      <label className="label pb-1">
                        <span className="label-text text-sm font-medium sm:text-base">
                          Order ID (if applicable)
                        </span>
                      </label>
                      <input
                        className="input input-sm input-bordered w-full sm:input-md"
                        name="tripId"
                        onChange={handleInputChange}
                        placeholder="e.g., HR123456789"
                        type="text"
                        value={formData.tripId}
                      />
                    </div>
                    <div className="form-control">
                      <label className="label pb-1">
                        <span className="label-text text-sm font-medium sm:text-base">
                          Location (if relevant)
                        </span>
                      </label>
                      <input
                        className="input input-sm input-bordered w-full sm:input-md"
                        name="location"
                        onChange={handleInputChange}
                        placeholder="e.g., Haka Lapeng"
                        type="text"
                        value={formData.location}
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="form-control">
                    <label className="label pb-1">
                      <span className="label-text text-sm font-medium sm:text-base lg:text-lg">
                        Subject
                      </span>
                    </label>
                    <input
                      className="input input-sm input-bordered w-full sm:input-md"
                      name="subject"
                      onChange={handleInputChange}
                      placeholder="Brief description of the issue"
                      required
                      type="text"
                      value={formData.subject}
                    />
                  </div>

                  {/* Description */}
                  <div className="form-control">
                    <label className="label pb-1">
                      <span className="label-text text-sm font-medium sm:text-base lg:text-lg">
                        Detailed Description
                      </span>
                    </label>
                    <textarea
                      className="textarea textarea-bordered textarea-sm h-24 w-full sm:textarea-md sm:h-32"
                      name="description"
                      onChange={handleInputChange}
                      placeholder="Please provide as much detail as possible about the issue, including what happened, when it occurred, and any steps you took..."
                      required
                      value={formData.description}
                    ></textarea>
                  </div>

                  {/* File Attachments */}
                  <div className="form-control">
                    <label className="label pb-1">
                      <span className="label-text text-sm font-medium sm:text-base">
                        Attachments (optional)
                      </span>
                    </label>
                    <input
                      accept="image/*,.pdf,.doc,.docx"
                      className="file-input file-input-bordered file-input-sm w-full sm:file-input-md"
                      multiple
                      name="attachments"
                      onChange={handleInputChange}
                      type="file"
                    />
                    <div className="label">
                      <span className="label-text-alt text-xs sm:text-sm">
                        <Camera className="mr-1 inline h-3 w-3 sm:h-4 sm:w-4" />
                        Upload screenshots, photos, or documents (Max 10MB each)
                      </span>
                    </div>
                  </div>

                  {apiError && (
                    <div className="alert alert-error mb-4">{apiError}</div>
                  )}

                  {/* Submit Button */}
                  <div className="form-control pt-3 sm:pt-4">
                    <button
                      className="btn btn-primary btn-sm w-full sm:btn-md lg:btn-lg"
                      disabled={isSubmitting}
                      type="submit"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="loading loading-spinner loading-sm"></span>
                          <span className="hidden sm:inline">
                            Submitting Report...
                          </span>
                          <span className="sm:hidden">Submitting...</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span className="hidden sm:inline">
                            Submit Issue Report
                          </span>
                          <span className="sm:hidden">Submit Report</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* API Error Message */}
                  {apiError && (
                    <div className="form-control">
                      <div className="alert alert-error shadow-lg">
                        <div>
                          <Bug size={20} />
                          <span>{apiError}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-base-100 py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <h2 className="mb-8 text-center text-2xl font-bold sm:mb-12 sm:text-3xl">
              Frequently Asked Questions
            </h2>
            <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
              <div className="collapse collapse-plus bg-base-200">
                <input name="faq-accordion" type="radio" />
                <div className="collapse-title text-base font-medium sm:text-lg lg:text-xl">
                  How quickly will my issue be resolved?
                </div>
                <div className="collapse-content">
                  <p className="text-sm sm:text-base">
                    Response times vary by priority: Urgent issues within 1
                    hour, High priority within 4 hours, Medium within 24 hours,
                    Low within 48 hours.
                  </p>
                </div>
              </div>

              <div className="collapse collapse-plus bg-base-200">
                <input name="faq-accordion" type="radio" />
                <div className="collapse-title text-base font-medium sm:text-lg lg:text-xl">
                  What information should I include in my report?
                </div>
                <div className="collapse-content">
                  <p className="text-sm sm:text-base">
                    Include as much detail as possible: Order ID, exact time and
                    location, screenshots, and step-by-step description of what
                    happened.
                  </p>
                </div>
              </div>

              <div className="collapse collapse-plus bg-base-200">
                <input name="faq-accordion" type="radio" />
                <div className="collapse-title text-base font-medium sm:text-lg lg:text-xl">
                  Can I track the status of my report?
                </div>
                <div className="collapse-content">
                  <p className="text-sm sm:text-base">
                    Yes, you will receive a reference number via email. You can
                    use this to track your report status and communicate with
                    our support team.
                  </p>
                </div>
              </div>

              <div className="collapse collapse-plus bg-base-200">
                <input name="faq-accordion" type="radio" />
                <div className="collapse-title text-base font-medium sm:text-lg lg:text-xl">
                  What if I need immediate assistance?
                </div>
                <div className="collapse-content">
                  <p className="text-sm sm:text-base">
                    For emergencies, call 121 (Police). For urgent non-emergency
                    issues, use our live chat feature or call our 24/7 support
                    hotline. For medical emergencies, call 122.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="text-center">
              <h2 className="mb-6 text-2xl font-bold sm:mb-8 sm:text-3xl">
                Other Ways to Reach Us
              </h2>
                <div className="grid gap-4 sm:gap-6 md:grid-cols-3">
                  <div className="card bg-base-100 shadow-lg">
                    <div className="card-body p-4 text-center sm:p-6">
                      <Phone className="mx-auto mb-3 h-6 w-6 text-primary sm:mb-4 sm:h-8 sm:w-8" />
                      <h3 className="card-title justify-center text-base sm:text-lg">
                        24/7 Support Hotline
                      </h3>
                      <p className="mb-3 text-sm text-base-content/70 sm:mb-4 sm:text-base">
                        0800-HAREENG
                      </p>
                      <p className="text-xs text-base-content/60 sm:text-sm">
                        Available 24 hours a day
                      </p>
                    </div>
                  </div>

                  <div className="card bg-base-100 shadow-lg">
                    <div className="card-body p-4 text-center sm:p-6">
                      <Mail className="mx-auto mb-3 h-6 w-6 text-secondary sm:mb-4 sm:h-8 sm:w-8" />
                      <h3 className="card-title justify-center text-base sm:text-lg">
                        Email Support
                      </h3>
                      <p className="mb-3 text-sm text-base-content/70 sm:mb-4 sm:text-base">
                        support@hareeng.org
                      </p>
                      <p className="text-xs text-base-content/60 sm:text-sm">
                        Response within 24 hours
                      </p>
                    </div>
                  </div>

                  <div className="card bg-base-100 shadow-lg">
                    <div className="card-body p-4 text-center sm:p-6">
                      <MessageSquare className="mx-auto mb-3 h-6 w-6 text-accent sm:mb-4 sm:h-8 sm:w-8" />
                      <h3 className="card-title justify-center text-base sm:text-lg">
                        Live Chat
                      </h3>
                      <p className="mb-3 text-sm text-base-content/70 sm:mb-4 sm:text-base">
                        In-app messaging
                      </p>
                      <p className="text-xs text-base-content/60 sm:text-sm">
                        Real-time assistance
                      </p>
                    </div>
                  </div>
                </div>

              {/* Always show email support for non-urgent issues */}
              <div className="mt-6 sm:mt-8">
                <div className="card bg-base-100 shadow-lg">
                  <div className="card-body p-4 sm:p-6">
                    <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
                      <Mail className="h-6 w-6 text-info sm:h-8 sm:w-8" />
                      <div className="text-center sm:text-left">
                        <h3 className="text-base font-semibold text-info sm:text-lg">
                          Email Support for Non-Urgent Issues
                        </h3>
                        <p className="text-sm text-base-content/70 sm:text-base">
                          For non-urgent matters:{" "}
                          <a
                            className="link link-primary"
                            href="mailto:support@hareeng.org"
                          >
                            support@hareeng.org
                          </a>
                        </p>
                        <p className="text-xs text-base-content/60 sm:text-sm">
                          We typically respond within 24 hours
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
